import { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { centsToPtbr } from '@/lib/formatters';
import type { PriceTier } from '@/schemas/commercial/price-table.schema';
import { PriceTierInput } from './price-tier-input';

type PriceRange = { from: number; to: number | null; unitPriceInCents: number };

const toPriceTiers = (priceRange?: PriceRange[]): PriceTier[] | undefined => {
  if (!priceRange || priceRange.length === 0) {
    return undefined;
  }

  return priceRange.map(({ from, to, unitPriceInCents }) => ({
    from,
    to: to === Number.MAX_SAFE_INTEGER ? null : to,
    unitPrice: unitPriceInCents / 100,
  }));
};

interface EquipmentPriceFormProps {
  equipmentModel: string;
  onPriceChange?: (prices: any) => void;
  initialData?: {
    creditPayment?: {
      type: 'batch' | 'unit';
      paymentType: 'credit' | 'upfront';
      unitPriceInCents: number;
      priceRange: Array<{ from: number; to: number | null; unitPriceInCents: number }>;
    };
    upfrontPayment?: {
      type: 'batch' | 'unit';
      paymentType: 'credit' | 'upfront';
      unitPriceInCents: number;
      priceRange: Array<{ from: number; to: number | null; unitPriceInCents: number }>;
    };
  };
  disabled?: boolean;
}

export function EquipmentPriceForm({
  equipmentModel,
  onPriceChange,
  initialData,
  disabled = false,
}: EquipmentPriceFormProps) {
  const [useQuantityRange, setUseQuantityRange] = useState(
    initialData?.creditPayment
      ? initialData.creditPayment.type === 'batch' ||
          initialData.creditPayment.priceRange.length > 0
      : false,
  );
  const [useCashQuantityRange, setUseCashQuantityRange] = useState(
    initialData?.upfrontPayment ? initialData.upfrontPayment.type === 'batch' : false,
  );
  const [singlePrice, setSinglePrice] = useState(
    initialData?.creditPayment &&
      initialData.creditPayment.type === 'unit' &&
      initialData.creditPayment.priceRange.length === 0
      ? centsToPtbr(initialData.creditPayment.unitPriceInCents)
      : '',
  );
  const [cashPrice, setCashPrice] = useState(
    initialData?.upfrontPayment &&
      initialData.upfrontPayment.type === 'unit' &&
      initialData.upfrontPayment.priceRange.length === 0
      ? centsToPtbr(initialData.upfrontPayment.unitPriceInCents)
      : '',
  );

  const initialCreditTiers = useRef(toPriceTiers(initialData?.creditPayment?.priceRange));
  const initialCashTiers = useRef(toPriceTiers(initialData?.upfrontPayment?.priceRange));

  const [priceTiers, setPriceTiers] = useState<PriceTier[] | undefined>(initialCreditTiers.current);
  const [cashPriceTiers, setCashPriceTiers] = useState<PriceTier[] | undefined>(
    initialCashTiers.current,
  );

  // Notify parent directly from event handlers.
  // Uses overrides because React state updates are async (state vars still hold old values).
  const notify = (changes: Record<string, unknown> = {}) => {
    const currentQuantityRange = (changes.useQuantityRange ?? useQuantityRange) as boolean;
    const currentCashQuantityRange = (changes.useCashQuantityRange ??
      useCashQuantityRange) as boolean;
    const currentPriceTiers = (
      changes.priceTiers !== undefined ? changes.priceTiers : priceTiers
    ) as PriceTier[] | undefined;
    const currentCashPriceTiers = (
      changes.cashPriceTiers !== undefined ? changes.cashPriceTiers : cashPriceTiers
    ) as PriceTier[] | undefined;

    onPriceChange?.({
      equipmentModel,
      useQuantityRange: currentQuantityRange,
      useCashQuantityRange: currentCashQuantityRange,
      singlePrice: (changes.singlePrice ?? singlePrice) as string,
      cashPrice: (changes.cashPrice ?? cashPrice) as string,
      priceTiers: currentQuantityRange && currentPriceTiers ? currentPriceTiers : [],
      cashPriceTiers:
        currentCashQuantityRange && currentCashPriceTiers ? currentCashPriceTiers : [],
    });
  };

  const creditToggleDisabled = disabled;
  const cashToggleDisabled = disabled;

  return (
    <div className="space-y-6 border border-gray-200 rounded-lg p-4">
      {/* Payment on Credit Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pagamento a prazo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quantity Range Toggle */}
          <div className="flex items-center space-x-3">
            <Switch
              checked={useQuantityRange}
              onCheckedChange={(checked) => {
                setUseQuantityRange(checked);
                notify({ useQuantityRange: checked });
              }}
              disabled={creditToggleDisabled}
              title="Faixa de quantidade"
            />
            <Label className="text-sm font-medium">Faixa de quantidade</Label>
          </div>

          {useQuantityRange ? (
            <PriceTierInput
              initialTiers={priceTiers ?? initialCreditTiers.current}
              onValidChange={(tiers) => {
                setPriceTiers(tiers);
                notify({ priceTiers: tiers });
              }}
              disabled={disabled}
            />
          ) : (
            /* Single Price Input */
            <div className="space-y-2">
              <Label className="text-sm">Preço por unidade</Label>
              <CurrencyInput
                value={singlePrice || '0,00'}
                onChange={(v) => {
                  setSinglePrice(v);
                  notify({ singlePrice: v });
                }}
                placeholder="0,00"
                className="max-w-xs"
                disabled={disabled}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment in Cash Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pagamento à vista</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quantity Range Toggle for Cash */}
          <div className="flex items-center space-x-3">
            <Switch
              checked={useCashQuantityRange}
              onCheckedChange={(checked) => {
                setUseCashQuantityRange(checked);
                notify({ useCashQuantityRange: checked });
              }}
              disabled={cashToggleDisabled}
              title="Faixa de quantidade"
            />
            <Label className="text-sm font-medium">Faixa de quantidade</Label>
          </div>

          {useCashQuantityRange ? (
            <PriceTierInput
              initialTiers={cashPriceTiers ?? initialCashTiers.current}
              onValidChange={(tiers) => {
                setCashPriceTiers(tiers);
                notify({ cashPriceTiers: tiers });
              }}
              disabled={disabled}
            />
          ) : (
            /* Single Cash Price Input */
            <div className="space-y-2">
              <Label className="text-sm">Preço por unidade</Label>
              <CurrencyInput
                value={cashPrice || '0,00'}
                onChange={(v) => {
                  setCashPrice(v);
                  notify({ cashPrice: v });
                }}
                placeholder="0,00"
                className="max-w-xs"
                disabled={disabled}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
